import * as api from '@apis/admin-credit-points';
import { useAuthentication } from '@hooks/authentication';
import { AdminCreditPointLog } from '@models/credit-points';
import { useCreditPointLogsStore } from '@stores/creditPointLogsStore';
import { useQuery } from '@tanstack/react-query';
import { localTimeToUtcISOString, utcTimeToLocalISOString } from '@utils/date';
import { Button, DatePicker, DatePickerProps, Input, Table } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

export const CreditPointLogsPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const searchInputRef = useRef<any>();

  const store = useCreditPointLogsStore();

  const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
    store.setStartDate(localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true }));
  };

  const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
    store.setEndDate(localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true }));
  };

  useAuthentication();

  const couponLogsQuery = useQuery({
    queryKey: [`coupon-logs`, JSON.stringify(store.searchQuery)],
    queryFn: () => api.searchCreditPointLogs(store.searchQuery),
  });

  const creditPointTemplatesQuery = useQuery({
    queryKey: [`admin-credit-point-templates`],
    queryFn: () => api.getCreditPointTemplates(),
  });

  const couponLogs = couponLogsQuery.data?.data;
  const creditPointTemplates = creditPointTemplatesQuery.data?.data || [];

  useEffect(() => {
    if (couponLogs) {
      store.setCreditPointLogsContainer(couponLogs);
    }
  }, [couponLogs]);

  const getExcelFile = async () => {
    const fileName = `igogo-credit-point-logs-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
    const blob = await api.searchCreditPointLogsExcelFile(store.searchQuery);
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      // IE variant
      (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
    } else {
      const url = window.URL.createObjectURL(blob.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode && link.parentNode.removeChild(link);
    }
  };

  const TABLE_COLUMNS: ColumnsType<AdminCreditPointLog> = [
    {
      title: 'ID',
      dataIndex: '_id',
      render: (_id) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(_id);
                toast.dark('ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              ID
            </Button>
          </div>
        );
      },
    },
    {
      title: '유저',
      render: (record: AdminCreditPointLog) => {
        return `${record.user?.profile?.name}(${record.user?.profile?.phone?.substring(
          record.user?.profile?.phone?.length - 4,
        )})`;
      },
    },
    {
      title: '내용',
      width: 300,
      render: (record: AdminCreditPointLog) => {
        if (!record.content) {
          return '-';
        }
        return record.content;
      },
    },
    {
      title: '포인트',
      width: 200,
      render: (record: AdminCreditPointLog) => {
        return record.amount;
      },
    },
    {
      title: '적립/사용',
      width: 100,
      render: (record: AdminCreditPointLog) => {
        return record.flag === '+' ? (
          <div className="badge" style={{ backgroundColor: '#d944db' }}>
            적립
          </div>
        ) : (
          <div className="badge" style={{ backgroundColor: '#3fb547' }}>
            사용
          </div>
        );
      },
    },
    {
      title: '생성',
      dataIndex: 'createdAt',
      render: (createdAt) => {
        if (!createdAt) {
          return '-';
        }
        return moment(createdAt).format('YYYY.MM.DD HH:mm');
      },
    },
  ];
  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>포인트 적립/사용 내역</div>
        <div style={{ display: 'flex' }}>
          <div style={{ marginLeft: '10px' }}>
            <DatePicker
              onChange={onChangeStartDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.startDate as any))}
            />
          </div>
          <div style={{ marginLeft: '8px' }}>
            <DatePicker
              onChange={onChangeEndDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.endDate as any))}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: '12px', width: '100%', height: '52px', alignItems: 'center' }}>
            <FilterSelect
              inactiveValue="all"
              value={store.searchQuery?.creditPointId || 'all'}
              style={{ width: 320 }}
              onChange={(value) => {
                store.updateSearchQuery({ creditPointId: value });
              }}
              options={[
                { value: 'all', label: '템플릿 전체' },
                ...creditPointTemplates.map((template) => {
                  return {
                    value: template._id,
                    label: `${template.creditPointName} - ${template.creditPoint.toLocaleString()}P`,
                  };
                }),
              ]}
            />
            <Input
              className={filterActiveClassName(!!store.searchQuery.keyword)}
              ref={searchInputRef}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  store.updateSearchQuery({ keyword: searchKeyword });
                }
              }}></Input>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setSearchKeyword(undefined);
                  store.resetSearchQuery();
                }}>
                검색 초기화
              </Button>
            </div>
            <div style={{ fontWeight: 'bold' }}>검색결과: {store.searchResultContainer.total}</div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto' }}>
              <Button
                style={{ marginLeft: '10px' }}
                onClick={() => {
                  getExcelFile();
                }}>
                검색결과 엑셀 파일 다운로드
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={store.searchResultContainer.contents.filter((product) => product?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={couponLogsQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CreditPointLogsPage;
